import { periodDragTaggingToCells } from "@/apis/table/route";
import { useTableId } from "@/components/mapping/hooks";
import queries from "@/queries";
import { useMainStore } from "@/stores/main";
import { CellType } from "@/stores/mapping";
import { CellsRange } from "@/types/spreadsheet";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";
import { getCellsFromRange } from "@/utils/cell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useWorkbookId } from "../use-workbook";

export const usePeriodDragTaggingMutationKey = (
  tableId: string | null,
  currentSelection: CellsRange | null,
) => {
  return ["period-drag-tagging", tableId, currentSelection?.address];
};

export default function usePeriodDragTagging(options?: {
  onSuccess?: () => void;
  onError?: () => void;
}) {
  const { data: workbookId } = useWorkbookId();

  const { data: tableId } = useTableId();

  const currentSelection = useMainStore((state) => state.selection);
  const queryClient = useQueryClient();
  const { data: cells } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId?.tableId!)
      ._ctx.cells(),
    enabled: !!workbookId && !!tableId?.tableId,
    select: (data) => {
      return data.cells.filter(
        (cell) => cell.type === CellType.DATA || cell.type === CellType.SUM,
      );
    },
  });

  const mutationKey = usePeriodDragTaggingMutationKey(
    tableId?.tableId,
    currentSelection.current,
  );

  const { mutate } = useMutation({
    mutationKey: mutationKey,
    mutationFn: async (params: {
      period?: AccountingPeriod | null;
      targetYear?: TargetYear | null;
      accumulated?: Accumulated | null;
    }) => {
      if (!workbookId) {
        throw new Error("Workbook ID is not found");
      }
      if (!tableId?.tableId) {
        throw new Error("Table ID is not found");
      }
      if (!cells) {
        throw new Error("Cells not found");
      }

      if (!currentSelection.current?.address.includes(":")) {
        throw new Error("Current selection is not a range");
      }

      const cellAddresses = getCellsFromRange(
        currentSelection.current.address,
      ).filter((cell) => {
        return cells?.find((c) => c.address.split(":")[0] === cell);
      });

      const response = await periodDragTaggingToCells({
        workbookId: workbookId,
        tableId: tableId.tableId,
        request: {
          cellAddresses: cellAddresses,
          period: params.period,
          targetYear: params.targetYear,
          accumulated: params.accumulated,
        },
      });

      queryClient.refetchQueries({
        queryKey: queries
          .workbook(workbookId)
          ._ctx.tables(tableId?.tableId!)
          ._ctx.cells().queryKey,
      });

      return response;
    },
    onSuccess: (data) => {
      options?.onSuccess?.();
      if (!workbookId || !tableId?.tableId) return;

      const tableCtx = queries
        .workbook(workbookId)
        ._ctx.tables(tableId.tableId);

      Object.keys(data.cells).forEach((cellAddress) => {
        const normalizedCellAddress = cellAddress.includes(":")
          ? cellAddress.split(":")[0]
          : cellAddress;

        queryClient.invalidateQueries({
          queryKey: tableCtx._ctx.cells(normalizedCellAddress)._ctx.period()
            .queryKey,
        });
      });

      queryClient.invalidateQueries({
        queryKey: tableCtx._ctx.editablePreview().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: tableCtx._ctx.editableTreeView().queryKey,
      });

      // 워크스페이스 셀 리스트 invalidate → period chip / 당기·전기 보더 재렌더
      queryClient.invalidateQueries({
        queryKey: tableCtx._ctx.cells().queryKey,
      });
    },
    onError: (error) => {
      toast.error(error.message);
      options?.onError?.();
    },
  });
  return { mutate };
}
